import { useScreenHelp } from "./ScreenHelp";
import React, { useEffect, useRef, useState } from "react";
import { cqcApi } from "./cqcApi";
import { definitions } from "./cqcFrameworks";
import "./cqc-workspace.css";
export default function CqcAddressForm({
  site = {},
  create = false,
  onSaved,
  onCancel,
}) {
  const initial = site.address || site.registeredAddress || {};
  const [address, setAddress] = useState({
    line1:
      typeof initial === "string"
        ? initial
        : initial.line1 || site.addressLine1 || "",
    line2: initial.line2 || "",
    town: initial.town || site.town || "",
    county: initial.county || "",
    postcode: initial.postcode || site.postcode || "",
    country: initial.country || "",
    countryOverrideReason: initial.countryOverrideReason || "",
  });
  const [name, setName] = useState(site.name || ""),
    [reference, setReference] = useState(site.registrationReference || ""),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [looking, setLooking] = useState(false),
    [lookup, setLookup] = useState(null),
    [error, setError] = useState("");
  useScreenHelp({ title: `Organisation address${site.name ? " — " + site.name : ""}`, blurb: "The service location’s address and postcode identify the country and the relevant care framework.", can: ["Enter the organisation’s full service address and postcode.", "Identify and review the country. If using a manual override, record why.", "Confirm the address and save it before configuring services."], related: ["care-profile"] }, 60);
  const lookupSerial = useRef(0);
  useEffect(() => {
    if (address.postcode && !address.country) identify();
    return () => {
      lookupSerial.current++;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  function change(key, value) {
    setAddress((a) => ({ ...a, [key]: value }));
    setConfirmed(false);
    if (key === "postcode") {
      lookupSerial.current++;
      setLooking(false);
      setLookup(null);
      setAddress((a) => ({ ...a, country: "", countryOverrideReason: "" }));
    }
  }
  async function identify() {
    const serial = ++lookupSerial.current,
      postcode = address.postcode;
    setLooking(true);
    setError("");
    try {
      const result = await cqcApi.resolveCountry(postcode);
      if (serial !== lookupSerial.current) return;
      setLookup(result);
      if (result.country)
        setAddress((a) => ({
          ...a,
          postcode: result.postcode,
          country: result.country,
        }));
      setConfirmed(false);
    } catch (e) {
      if (serial === lookupSerial.current) setError(e.message);
    } finally {
      if (serial === lookupSerial.current) setLooking(false);
    }
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      const payload = { ...address, confirmed },
        result = create
          ? await cqcApi.createOrganisation({
              name,
              registrationReference: reference,
              address: payload,
            })
          : await cqcApi.saveAddress(
              site._id,
              payload,
              site.addressRevision || 0,
            );
      if (onSaved) await onSaved(result.site);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const manual = !lookup?.country || lookup.country !== address.country;
  return (
    <div className="cqc-workspace">
      <h2>
        {create ? "Add an organisation" : `Organisation address — ${site.name}`}
      </h2>
      <p>
        Use the address where this service is based, rather than a group’s
        head-office address. It determines the national framework suggested for
        this location.
      </p>
      {error && (
        <p className="cqc-error" role="alert">
          {error}
        </p>
      )}
      <section className="cqc-card">
        {create && (
          <>
            <p>
              <label>
                Organisation name{" "}
                <input
                  style={{ width: "100%" }}
                  value={name}
                  maxLength={200}
                  onChange={(e) => {
                    setName(e.target.value);
                    setConfirmed(false);
                  }}
                />
              </label>
            </p>
            <p>
              <label>
                Regulator registration reference (if known){" "}
                <input
                  style={{ width: "100%" }}
                  maxLength={200}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </label>
            </p>
          </>
        )}
        {[
          ["line1", "Address line 1"],
          ["line2", "Address line 2"],
          ["town", "Town / city"],
          ["county", "County"],
          ["postcode", "Postcode"],
        ].map(([key, label]) => (
          <p key={key}>
            <label>
              {label}
              <input
                style={{ width: "100%" }}
                maxLength={200}
                value={address[key]}
                onChange={(e) => change(key, e.target.value)}
                onBlur={
                  key === "postcode"
                    ? () => {
                        if (address.postcode.trim() && !lookup && !looking)
                          identify();
                      }
                    : undefined
                }
              />
            </label>
          </p>
        ))}
        <button
          className="cqc-button"
          disabled={!address.postcode.trim() || looking || busy}
          onClick={identify}
        >
          {looking ? "Looking up…" : "Identify country from postcode"}
        </button>
        {lookup && (
          <p className={lookup.country ? "cqc-muted" : "cqc-warning"}>
            {lookup.country
              ? `Postcode country: ${lookup.country}`
              : "Country could not be verified automatically."}{" "}
            · {lookup.method}
          </p>
        )}
        <p>
          <label>
            Country{" "}
            <select
              aria-label="Country"
              value={address.country}
              onChange={(e) => change("country", e.target.value)}
            >
              <option value="">Select / confirm country</option>
              {definitions.countries.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
        </p>
        {manual && (
          <p>
            <label>
              Reason for manual country confirmation / postcode override{" "}
              <textarea
                style={{ width: "100%" }}
                maxLength={1000}
                value={address.countryOverrideReason}
                onChange={(e) =>
                  change("countryOverrideReason", e.target.value)
                }
              />
            </label>
          </p>
        )}
        <p className="cqc-muted">
          Full-postcode lookup avoids guessing from border postcode areas. If
          lookup is unavailable or the result differs from the service location,
          record why you are confirming another country.
        </p>
        <label>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />{" "}
          I confirm this is the service address and the country is correct.
        </label>
      </section>
      <div className="cqc-toolbar">
        {onCancel && (
          <button className="cqc-button" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          className="cqc-button active"
          disabled={
            busy ||
            looking ||
            !confirmed ||
            !address.line1.trim() ||
            !address.town.trim() ||
            !address.postcode.trim() ||
            !address.country ||
            (create && !name.trim())
          }
          onClick={save}
        >
          {busy
            ? "Saving…"
            : create
              ? "Add organisation"
              : "Save address and country"}
        </button>
      </div>
    </div>
  );
}
